/**
 * WebSocket service for real-time updates
 */

import {
  WebSocketMessage,
  RunStateChangedMessage,
  GradeCompletedMessage,
  TTLExtendedMessage,
  ErrorOccurredMessage,
  AssignmentCompletedMessage,
  AssignmentCreatedMessage,
} from '../types';

export type WebSocketEventHandler = (message: WebSocketMessage) => void;
export type RunStateChangeHandler = (message: RunStateChangedMessage) => void;
export type GradeCompleteHandler = (message: GradeCompletedMessage) => void;
export type TTLExtendHandler = (message: TTLExtendedMessage) => void;
export type ErrorHandler = (message: ErrorOccurredMessage) => void;
export type AssignmentCompletedHandler = (message: AssignmentCompletedMessage) => void;
export type AssignmentCreatedHandler = (message: AssignmentCreatedMessage) => void;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 1000; // Start with 1 second
  private handlers = new Map<string, Set<WebSocketEventHandler>>();
  private isConnected = false;
  private shouldReconnect = true;

  constructor() {
    // Prefer explicit WebSocket URL if provided
    const explicitWs = process.env.REACT_APP_WS_URL;
    if (explicitWs && explicitWs.startsWith('ws')) {
      this.url = explicitWs;
      return;
    }

    // Otherwise derive from API URL and current protocol
    const currentProtocol = window.location.protocol;

    let apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    if (currentProtocol === 'https:' && apiUrl.startsWith('http://')) {
      apiUrl = apiUrl.replace('http://', 'https://');
    } else if (currentProtocol === 'http:' && apiUrl.startsWith('https://')) {
      apiUrl = apiUrl.replace('https://', 'http://');
    }

    const wsProtocol = currentProtocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = apiUrl.replace(/^https?/, wsProtocol);
    this.url = `${wsUrl}/api/ws`;
  }

  connect(token: string): void {
    this.token = token;
    this.shouldReconnect = true;
    this.createConnection();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.cleanup();
  }

  private createConnection(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const wsUrl = this.token ? `${this.url}?token=${this.token}` : this.url;
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  private handleOpen(): void {
    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.reconnectInterval = 1000;
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      this.dispatchMessage(message);
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  private handleClose(event: CloseEvent): void {
    this.isConnected = false;
    
    if (this.shouldReconnect && event.code !== 1000) {
      this.scheduleReconnect();
    }
  }

  private handleError(error: Event): void {
    console.error('WebSocket error:', error);
    // Trigger reconnection on error if we should reconnect
    if (this.shouldReconnect && !this.isConnected) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    setTimeout(() => {
      this.reconnectAttempts++;
      this.createConnection();
      
      // Exponential backoff
      this.reconnectInterval = Math.min(this.reconnectInterval * 2, 30000);
    }, this.reconnectInterval);
  }

  private cleanup(): void {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.isConnected = false;
  }

  private dispatchMessage(message: WebSocketMessage): void {
    const handlers = this.handlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in WebSocket message handler:', error);
        }
      });
    }

    // Also dispatch to 'all' handlers
    const allHandlers = this.handlers.get('all');
    if (allHandlers) {
      allHandlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in WebSocket message handler:', error);
        }
      });
    }
  }

  // Event subscription methods
  on(eventType: string, handler: WebSocketEventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.handlers.get(eventType);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.handlers.delete(eventType);
        }
      }
    };
  }

  // Typed event handlers
  onRunStateChanged(handler: RunStateChangeHandler): () => void {
    return this.on('run_state_changed', handler as WebSocketEventHandler);
  }

  onGradeCompleted(handler: GradeCompleteHandler): () => void {
    return this.on('grade_completed', handler as WebSocketEventHandler);
  }

  onTTLExtended(handler: TTLExtendHandler): () => void {
    return this.on('ttl_extended', handler as WebSocketEventHandler);
  }

  onError(handler: ErrorHandler): () => void {
    return this.on('error_occurred', handler as WebSocketEventHandler);
  }

  onAssignmentCompleted(handler: AssignmentCompletedHandler): () => void {
    return this.on('assignment_completed', handler as WebSocketEventHandler);
  }

  onAssignmentCreated(handler: AssignmentCreatedHandler): () => void {
    return this.on('assignment_created', handler as WebSocketEventHandler);
  }

  onAll(handler: WebSocketEventHandler): () => void {
    return this.on('all', handler);
  }

  // Send message to server (if needed in the future)
  send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  // Connection state
  get connected(): boolean {
    return this.isConnected;
  }

  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }
}

export const webSocketService = new WebSocketService();
